import "react-international-phone/style.css";
import { Alert, Anchor, Button, Input, Modal, Stack, Text, Textarea, TextInput } from "@mantine/core";
import { Form, useNavigation } from "@remix-run/react";
import { useEffect, useId, useState } from "react";
import { PhoneInput } from "react-international-phone";
import { useTranslation } from "~/hooks/use-typed-translation";
import type { JoinActionData } from "./registration.server";
import classes from "~/routes/($lang).join/join.module.scss";

export function ProfessionalRegistration({ submissionId, result }: {
  submissionId: string; result?: JoinActionData;
}) {
  const { t } = useTranslation();
  const navigation = useNavigation();
  const formId = useId();
  const busy = navigation.state !== "idle";
  const [phone, setPhone] = useState("");
  const [editing, setEditing] = useState(false);
  const [otp, setOtp] = useState("");
  const [deadline, setDeadline] = useState(0);
  const [seconds, setSeconds] = useState(0);
  const verifying = result?.step === "verify" && !editing;
  useEffect(() => {
    setEditing(false);
    setOtp("");
    if (result?.sentAt) setDeadline(result.sentAt + 60000);
  }, [result]);
  useEffect(() => {
    const tick = () => setSeconds(Math.max(0, Math.ceil((deadline - Date.now()) / 1000)));
    tick();
    const timer = window.setInterval(tick, 1000);
    return () => window.clearInterval(timer);
  }, [deadline]);
  const editDetails = () => {
    if (busy) return;
    setEditing(true);
    setOtp("");
  };
  return <>
  <Form id={formId} method="post">
    <Stack>
      <input type="hidden" name="submissionId" value={result?.submissionId || submissionId} />
      <div aria-hidden="true" style={{ position: "absolute", left: -10000 }}>
        <label htmlFor="professional-website">Website</label>
        <input id="professional-website" name="website" tabIndex={-1} autoComplete="off" />
      </div>
      {!verifying && !editing && result?.message && <Alert role="status" color={result.sentAt ? "blue" : "red"}>{result.message}</Alert>}
      <TextInput name="name" label={t("referral-join-full-name")} required minLength={2} maxLength={180} autoComplete="name" readOnly={verifying} disabled={busy} />
      <Input.Wrapper label={t("professional-join-whatsapp")} description={t("professional-join-whatsapp-help")} required>
        <PhoneInput name="phoneNumber" defaultCountry="ci" value={phone} onChange={setPhone}
          disabled={busy || verifying} required
          className={classes["react-international-phone"]}
          inputClassName={classes["react-international-phone-input"]}
          inputProps={{ autoComplete: "tel", maxLength: 40, "aria-label": t("professional-join-whatsapp") }} />
        {(busy || verifying) && <input type="hidden" name="phoneNumber" value={phone} />}
      </Input.Wrapper>
      <TextInput name="email" type="email" label={t("referral-join-email")} autoComplete="email" maxLength={254} readOnly={verifying} disabled={busy} />
      <TextInput name="specialty" label={t("referral-join-specialty")} maxLength={180} readOnly={verifying} disabled={busy} />
      <TextInput name="socialHandle" label={t("professional-join-social")} maxLength={180} readOnly={verifying} disabled={busy} />
      <Textarea name="notes" label={t("professional-join-notes")} maxLength={2000} readOnly={verifying} disabled={busy} />
      <Button type="submit" name="intent" value="send" loading={busy} disabled={busy || seconds > 0 || verifying}>
        {seconds > 0 ? t("professional-join-resend-wait", { seconds }) : t("professional-join-send")}
      </Button>
      <Text size="xs" c="dimmed">{t("referral-join-privacy-prefix")}{" "}
        <Anchor href="https://docs.google.com/document/d/1amefCfTGw-erveo2ILEYqe_Ppvb0VXYDjjq0lE32f1A/edit?usp=sharing" target="_blank" rel="noreferrer" inherit>{t("referral-join-privacy-link")}</Anchor>.
      </Text>
    </Stack>
  </Form>
  <Modal opened={verifying} onClose={editDetails} title={t("professional-join-code")} centered
    closeOnClickOutside={!busy} closeOnEscape={!busy} withCloseButton={!busy}>
    <Stack>
      {result?.message && <Alert role="status" color={result.sentAt ? "blue" : "red"}>{result.message}</Alert>}
      <Text size="sm">{t("professional-join-code-help", { phone })}</Text>
      {/* Mantine portals the modal outside the form. Explicit form ownership
          keeps the OTP and submit buttons attached to the application details. */}
      <TextInput form={formId} name="otp" label={t("professional-join-code")} value={otp}
        onChange={event => setOtp(event.currentTarget.value.replace(/\D/g, ""))}
        autoComplete="one-time-code" inputMode="numeric" maxLength={8} disabled={busy} data-autofocus
        onKeyDown={event => {
          if (event.key === "Enter") {
            event.preventDefault();
            if (!busy && otp.length >= 4) document.getElementById(`${formId}-verify`)?.click();
          }
        }} />
      <Button id={`${formId}-verify`} form={formId} type="submit" name="intent" value="verify"
        loading={busy} disabled={busy || otp.length < 4}>{t("professional-join-verify-submit")}</Button>
      <Button form={formId} type="submit" name="intent" value="send" variant="light" disabled={busy || seconds > 0}>
        {seconds > 0 ? t("professional-join-resend-wait", { seconds }) : t("professional-join-resend")}
      </Button>
      <Button type="button" variant="subtle" disabled={busy} onClick={editDetails}>{t("professional-join-edit")}</Button>
    </Stack>
  </Modal>
  </>;
}
